import {useBoxDimensions} from "../windowDimensions";
import React, {useId, useMemo, useState} from "react";
import FullscreenSpinner from "../FullscreenSpinner";
import RangePlot from "../plots/RangePlot";
import {Card, Col, Form, Row, Button, Dropdown, Toast} from "react-bootstrap";
import {useMutation, useQueryClient} from "react-query";
import {postWearableConfig, patchWearableConfig} from "../api";
import {useNavigate} from "react-router-dom";
import moment from "moment-timezone";


function RadioGroup(props) {
    const {options, selectedValue, onChangeSelected} = props;
    const groupId = useId();


    return options.map((option, i) =>
        <Form.Check
            inline
            key={i}
            label={option.label}
            name={`group-${groupId}`}
            type="radio"
            id={`radio-${groupId}-${i}`}
            checked={selectedValue === option.value}
            onClick={e => onChangeSelected(option.value)}
        />
    );
}

function DropdownGroup(props) {
    const {options, selectedValue, onChangeSelected} = props;

    const selectedOption = options.find(option => selectedValue === option.value);

    return <Dropdown>
        <Dropdown.Toggle variant="secondary" id="dropdown-basic">
            {selectedOption !== undefined ? selectedOption?.label : "<Not Set>"}
        </Dropdown.Toggle>

        <Dropdown.Menu>
            {
                options.map((option, i) =>
                    <Dropdown.Item key={i} onClick={e => onChangeSelected(option.value)}>{option.label}</Dropdown.Item>
                )
            }
        </Dropdown.Menu>
    </Dropdown>
}

function WearableConfigView(props) {
    const {organization, wearable, wearableMetrics, context} = props;
    const {wearablesQuery} = context;

    // TODO QA
    const [form, setForm] = useState(wearable.config.json);

    const timezones = useMemo(() => {
        const supportedZones = moment.tz.names().filter(name => name.includes("Etc/GMT"));
        const zonesOptions = supportedZones.flatMap(zoneName => {
            const zoneObject = moment.tz.zone(zoneName);
            if (zoneObject.offsets.length === 1){
                return [{value: zoneObject.offsets[0], label: zoneObject.name}];
            } else {
                return [];
            }

        });
        const zonesOptionsUnique = [
            ...new Map(zonesOptions.map((item) => [item.value, item])).values(),
        ];
        zonesOptionsUnique.sort((zone1, zone2) => zone1.value - zone2.value);
        return zonesOptionsUnique;
    }, []);


    const queryClient = useQueryClient()
    const navigate = useNavigate();

    const mutation = useMutation(configUpdate => {
        return patchWearableConfig(configUpdate.organizationId, configUpdate.wearableId, configUpdate.config);
    }, {
        onError: (error, variables, context) => {

        },
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries();
            wearablesQuery.refetch();
            navigate(`/org/${organization.id}/`);
        },
        onSettled: (data, error, variables, context) => {
            console.log({data, event: "Settled"});
        }
    })

    function keyHandler(e) {
        e.stopPropagation();
    }


    function submit(e) {
        e.preventDefault();

        // Reset Heart Rate Duty Cycle to default (10 mins) if 0 or Null
        if (form.hrDutyCycle === 0 || !form.hrDutyCycle){
            form.hrDutyCycle = 10;
        };

        // Reset Sleep Time to default (21 = 9PM) if Null
        if (!form.sleepTime){
            form.sleepTime = 21
        }

        if (mutation.isError) {
            mutation.reset();
        }
        const result = mutation.mutate({organizationId: organization.id, wearableId: wearable.id, config: form});

    }

    if (mutation.isLoading) {
        return <FullscreenSpinner/>;
    }
    return <div>
        <Toast onClose={() => {
        }} show={mutation.isError} animation={false}>
            <Toast.Body>Failed to save. Please try again.</Toast.Body>
        </Toast>
        <Row>
            <Col md={{span: 8, offset: 2}}>
                <Form onSubmit={submit} onKeyDown={keyHandler}>
                    <Form.Group className="mb-3" controlId="formName">
                        <Form.Label><b>Name</b></Form.Label>
                        <Form.Control
                            type="name" placeholder="name"
                            value={form.name}
                            onChange={e => {
                                setForm(prevForm => {
                                    return {...prevForm, name: e.target.value};
                                })
                            }}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formAge">
                        <Form.Label><b>Age</b></Form.Label>
                        <Form.Control
                            type="number" placeholder="age"
                            value={form.age}
                            onChange={e => {
                                setForm(prevForm => {
                                    return {...prevForm, age: parseInt(e.target.value)};
                                })
                            }}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formWeight">
                        <Form.Label><b>Weight (kg)</b></Form.Label>
                        <Form.Control
                            type="number" placeholder="weight"
                            value={form.weight}
                            onChange={e => {
                                setForm(prevForm => {
                                    return {...prevForm, weight: parseInt(e.target.value)};
                                })
                            }}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formHeight">
                        <Form.Label><b>Height (mm)</b></Form.Label>
                        <Form.Control
                            type="number" placeholder="height"
                            value={form.height}
                            onChange={e => {
                                setForm(prevForm => {
                                    return {...prevForm, height: parseInt(e.target.value)};
                                })
                            }}
                        />
                    </Form.Group>

                    <Form.Group>
                        <Form.Label><b>Clock Format</b></Form.Label>
                        <div className="mb-3">
                            <RadioGroup
                                selectedValue={form.clockFormat}
                                options={[
                                    {value: 0, label: "12-hour"},
                                    {value: 1, label: "24-hour"},
                                ]}
                                onChangeSelected={value => setForm(prevForm => {
                                    return {...prevForm, clockFormat: value};
                                })}
                            />
                        </div>
                    </Form.Group>

                    <Form.Group>
                        <Form.Label><b>Heart Rate Duty Cycle</b></Form.Label>
                        <div className="mb-3">
                            <p>{form.hrDutyCycle === 0 ?
                                    10 :
                                    form.hrDutyCycle ?
                                    form.hrDutyCycle :
                                    10} {form.hrDutyCycle < 2 ? 'Minute': 'Minutes'}</p>
                            <Form.Range
                                value={form.hrDutyCycle === 0 ?
                                    10 :
                                    form.hrDutyCycle ?
                                    form.hrDutyCycle :
                                    10
                                }
                                min = {1}
                                max = {20}
                                onChange={e => setForm(prevForm => {
                                    if(e.target.value > 10 && e.target.value < 15){
                                        e.target.value = 15;
                                    }
                                    if (e.target.value > 15 && e.target.value < 20){
                                        e.target.value = 20;
                                    }
                                    if (e.target.value > 20 && e.target.value < 25){
                                        e.target.value = 25;
                                    }
                                    return {...prevForm, hrDutyCycle: e.target.value};
                                })}
                            />
                        </div>
                    </Form.Group>

                    <Form.Group>
                        <Form.Label><b>Language</b></Form.Label>
                        <div className="mb-3">
                            <DropdownGroup
                                selectedValue={form.language}
                                options={[
                                    {value: 0, label: "English"},
                                    {value: 1, label: "Simplified Chinese"},
                                    {value: 2, label: "Japanese"},
                                    {value: 3, label: "Russian"},
                                    {value: 4, label: "German"},
                                    {value: 5, label: "French"},
                                    {value: 6, label: "Italian"},
                                    {value: 7, label: "Spanish"},
                                    {value: 8, label: "Portuguese"},
                                    {value: 9, label: "Korean"},
                                    {value: 10, label: "Traditional Chinese"},
                                    {value: 11, label: "Czech"}
                                ]}
                                onChangeSelected={value => setForm(prevForm => {
                                    return {...prevForm, language: value};
                                })}
                            />
                        </div>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label><b>Units</b></Form.Label>
                        <div className="mb-3">
                            <RadioGroup
                                selectedValue={form.units}
                                options={[
                                    {value: 0, label: "Imperial"},
                                    {value: 1, label: "Metric"}
                                ]}
                                onChangeSelected={value => setForm(prevForm => {
                                    return {...prevForm, units: value};
                                })}
                            />
                        </div>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label><b>Gender</b></Form.Label>
                        <div className="mb-3">
                            <RadioGroup
                                selectedValue={form.gender}
                                options={[
                                    {value: 0, label: "Male"},
                                    {value: 1, label: "Female"}
                                ]}
                                onChangeSelected={value => setForm(prevForm => {
                                    return {...prevForm, gender: value};
                                })}
                            />
                        </div>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label><b>Timezone</b></Form.Label>
                        <div className="mb-3">
                            <DropdownGroup
                                selectedValue={form.timezone}
                                options={timezones}
                                onChangeSelected={value => setForm(prevForm => {
                                    return {...prevForm, timezone: value};
                                })}
                            />
                        </div>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="formCalories">
                        <Form.Label><b>Calories Goal (kilocalories)</b></Form.Label>
                        <Form.Control
                            type="number" placeholder="calories goal"
                            value={form.caloriesGoal}
                            onChange={e => {
                                setForm(prevForm => {
                                    return {...prevForm, caloriesGoal: parseInt(e.target.value)};
                                })
                            }}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="formDistance">
                        <Form.Label><b>Distance Goal (meters)</b></Form.Label>
                        <Form.Control
                            type="number" placeholder="distance goal"
                            value={form.distanceGoal}
                            onChange={e => {
                                setForm(prevForm => {
                                    return {...prevForm, distanceGoal: parseInt(e.target.value)};
                                })
                            }}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="formSteps">
                        <Form.Label><b>Steps Goal</b></Form.Label>
                        <Form.Control
                            type="number" placeholder="steps goal"
                            value={form.stepsGoal}
                            onChange={e => {
                                setForm(prevForm => {
                                    return {...prevForm, stepsGoal: parseInt(e.target.value)};
                                })
                            }}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="formSleep">
                        <Form.Label><b>Sleep Goal (minutes)</b></Form.Label>
                        <Form.Control
                            type="number" placeholder="sleep goal"
                            value={form.sleepGoal}
                            onChange={e => {
                                setForm(prevForm => {
                                    return {...prevForm, sleepGoal: parseInt(e.target.value)};
                                })
                            }}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label><b>Sleep Time</b></Form.Label>
                        <div className="mb-3">
                            {form.sleepTime ? (form.clockFormat === 0) ?
                                <p>{form.sleepTime > 12 ? form.sleepTime - 12 : form.sleepTime} {form.sleepTime >= 12 ? 'PM' : 'AM'}</p>
                                    :
                                <p>{form.sleepTime} O'Clock</p> 
                            :
                            (form.sleepTime = 21) 
                            }
                            <Form.Range
                                value={form.sleepTime || 21}
                                min = {0}
                                max = {23}
                                onChange={e => setForm(prevForm => {
                                    return {...prevForm, sleepTime: e.target.value};
                                })}
                            />
                        </div>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="formAlert">
                        <Form.Label><b>Alert</b></Form.Label>
                        <Form.Check
                            type="switch"
                            checked={form.firmwareMode === 2 ? true :
                                form.firmwareMode === 6 ? true :
                                form.firmwareMode === 18 ? true :
                                form.firmwareMode === 22 ? true :
                                false
                            }
                            label="SOS"
                            onChange={e => {
                                setForm(prevForm => {
                                    // For enabling SOS
                                    if (e.target.checked && prevForm.firmwareMode < 2){ //can only go to MODE_SOS_ENABLE = 0x02
                                        return {...prevForm, firmwareMode: parseInt(2)}
                                    } else if (e.target.checked && prevForm.firmwareMode < 6){//can only go to MODE_SOS_FALL_ENABLE = 0x06
                                        return {...prevForm, firmwareMode: parseInt(6)}
                                    }else if (e.target.checked && prevForm.firmwareMode < 18){//can only go to MODE_SOS_TACH_ENABLE = 0x12
                                        return {...prevForm, firmwareMode: parseInt(18)}
                                    } else if (e.target.checked && prevForm.firmwareMode < 22){//can only go to MODE_SOS_FALL_TACH_ENABLE = 0x16
                                        return {...prevForm, firmwareMode: parseInt(22)}
                                    }

                                    //For disenabling SOS
                                    if (!e.target.checked && prevForm.firmwareMode > 16){ //go from MODE_SOS_FALL_TACH_ENABLE = 0x16/MODE_SOS_TACH_ENABLED = 0x12 to MODE_TACH_ENABLE = 0x10
                                        return {...prevForm, firmwareMode: parseInt(16)}
                                    } else if (!e.target.checked && prevForm.firmwareMode > 4){//go from MODE_SOS_FALL_ENABLE = 0x06 to MODE_FALL_ENABLE = 0x04
                                        return {...prevForm, firmwareMode: parseInt(4)}
                                    }else if (!e.target.checked && prevForm.firmwareMode > 1){//go from MODE_SOS_ENABLED = 0x02 to MODE_NONE_ENABLE = 0x00
                                        return {...prevForm, firmwareMode: parseInt(0)}
                                    }
                                })
                            }}
                        />
                        <Form.Check
                            type="switch"
                            value={form.firmwareMode ? true : false}
                            label="Fall"
                            onChange={e => {
                                setForm(prevForm => {
                                    // For enabling Fall
                                    if (e.target.checked && prevForm.firmwareMode < 2){ //can only go to MODE_Fall_ENABLE = 0x04
                                        return {...prevForm, firmwareMode: parseInt(4)}
                                    } else if (e.target.checked && prevForm.firmwareMode < 6){//can only go to MODE_SOS_FALL_ENABLE = 0x06
                                        return {...prevForm, firmwareMode: parseInt(6)}
                                    }else if (e.target.checked && prevForm.firmwareMode < 22){//can only go to MODE_SOS_TACH_ENABLE = 0x12
                                        return {...prevForm, firmwareMode: parseInt(22)}
                                    }

                                    //For disenabling Fall
                                    if (!e.target.checked && prevForm.firmwareMode > 22){ //go to from MODE_SOS_FALL_TACH_ENABLE = 0x16 to MODE_SOS_TACH_ENABLE = 0x12
                                        return {...prevForm, firmwareMode: parseInt(18)}
                                    } else if (!e.target.checked && prevForm.firmwareMode > 4){//go from MODE_SOS_FALL_ENABLE = 0x06 to MODE_SOS_ENABLE = 0x02
                                        return {...prevForm, firmwareMode: parseInt(2)}
                                    }else if (!e.target.checked && prevForm.firmwareMode > 2){//go to MODE_FALL_ENABLE = 0x04 to MODE_NONE_ENABLE = 0x00
                                        return {...prevForm, firmwareMode: parseInt(0)}
                                    }
                                })
                            }}
                        />
                    </Form.Group>
                    <Button variant="primary" type="submit">
                        Save
                    </Button>
                </Form>
            </Col>
        </Row>
    </div>;
}

export default WearableConfigView;